import { CharacterAvatar } from "@/components/character/CharacterAvatar";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import type { CharacterWithTags } from "@/types/character";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronDown, MessageCircle, Star } from "lucide-react";
import { Link } from "react-router-dom";

interface CharacterCardProps {
  character: CharacterWithTags;
  index: number;
}

const CharacterCard = ({ character, index }: CharacterCardProps) => {
  return (
    <Card
      className="group cursor-pointer bg-white/90 backdrop-blur-xs border-0 shadow-lg hover:shadow-2xl transition-all duration-500"
      style={{ animationDelay: `${index * 150}ms` }}
    >
      <CardHeader className="text-center pb-1">
        <div className="relative mx-auto mb-3">
          <CharacterAvatar
            name={character.name}
            avatar_url={character.avatar_url ?? undefined}
            className={`w-36 h-36`}
          />
        </div>

        <div className="flex items-center justify-center mb-">
          <div className="flex items-center space-x-1">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className={`w-4 h-4 ${
                  i < Math.floor(character.rating)
                    ? "text-sylvain-blue fill-current"
                    : "text-gray-300"
                }`}
              />
            ))}
            <span className="text-sm text-gray-600 ml-1">
              {character.rating}
            </span>
          </div>
        </div>

        <CardTitle className="text-lg mb-1">
          {character.name}
        </CardTitle>
        <CardDescription className="font-medium text-sylvain-blue line-clamp-2 min-h-[2lh]">
          {character.title}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-3 pt-1">
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="details">
            <AccordionPrimitive.Header className="flex justify-center">
              <AccordionPrimitive.Trigger asChild className="group">
                <Button variant="ghost">
                  <ChevronDown className="w-4 h-4 mr-2 group-data-[state=open]:rotate-180 transition-all" />
                  Voir plus
                </Button>
              </AccordionPrimitive.Trigger>
            </AccordionPrimitive.Header>
            <AccordionContent>
              <div className="space-y-3 ">
                <p className="text-gray-600 leading-relaxed text-center text-sm">
                {character.short_description}
                </p>
                <div>
                  <h4 className="font-semibold mb-2 text-center text-sm">
                    Spécialités
                  </h4>
                  <div className="flex flex-wrap gap-1 justify-center">
                    {character.tags?.map((tag) => (
                      <span
                        key={tag.id}
                        className="px-2 py-1 bg-linear-to-r from-blue-50 to-indigo-50 text-blue-700 rounded-full text-sm font-medium border border-blue-200"
                      >
                        {tag.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        <div className="space-y-2">
          <Button asChild className="w-full shadow-lg hover:shadow-xl">
            <Link to={`/chat/${character.id}`}>
              <MessageCircle className="w-4 h-4 mr-1" />
              C'est parti !
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default CharacterCard;
